import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import HeroImage from './HeroImage';
import { HeroData } from '../../../models/HeroData';
import { useDraft } from '../contexts/DraftContext';
import DraftState from '../../../models/DraftState';

const OverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 20px;
  max-width: 1800px;
  width: 100%;
  height: 90vh;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10001;

  &:hover {
    background-color: #d32f2f;
    transform: scale(1.1);
  }
`;

const ScenarioBoardContainer = styled.div`
  flex: 0 1 45%;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  border: 3px solid #1de9b6;
  min-width: 0;
`;

const ScenarioTitle = styled.h2`
  color: #1de9b6;
  font-size: 16px;
  margin-bottom: 15px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TeamsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 6px;
  align-items: center;
`;

const TeamHeader = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
  text-align: center;
  width: 50%;
  text-shadow: 0 0 8px ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
`;

const PicksContainer = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
  gap: 16px;
  background: linear-gradient(to bottom, rgba(20, 22, 34, 0.8), rgba(14, 16, 26, 0.8));
  padding: 12px 0;
  border-radius: 8px;
`;

const getBoxSize = (position) => {
  if (DraftState.getLargeBoxSequences().includes(position.toString())) {
    return '65px';
  }
  return '50px';
};

const TeamColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => {
    const isRadiant = props.$team === 'Radiant';
    const isSwapped = props.$swapped || false;
    const isOnLeft = isSwapped ? !isRadiant : isRadiant;
    return isOnLeft ? 'flex-end' : 'flex-start';
  }};
  gap: 12px;
`;

const PickSlot = styled.div`
  border: 1px solid #444;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${props => getBoxSize(props.$position)};
  width: ${props => {
    if (DraftState.getLargeBoxSequences().includes(props.$position.toString())) {
      return '120px';
    }
    return '80px';
  }};
  position: relative;
  z-index: 3;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #111215;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  padding: 0;

  &:hover {
    border-color: ${props => props.$team === 'Radiant' ? '#92ff92' : '#ff6b6b'};
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
  }

  &.filled {
    background-color: ${props => props.$team === 'Radiant' ? 'rgba(146, 255, 146, 0.1)' : 'rgba(255, 107, 107, 0.1)'};
  }
`;

const PositionLabel = styled.div`
  position: absolute;
  top: 2px;
  left: 2px;
  font-size: 9px;
  font-weight: bold;
  color: ${props => props.$isBan ? '#ff6b6b' : '#6bff74'};
  background-color: rgba(0, 0, 0, 0.5);
  padding: 1px 3px;
  border-radius: 3px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  z-index: 2;
`;

const BanCross = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff6b6b;
  font-weight: bold;
  font-size: 18px;
  pointer-events: none;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.9);
  z-index: 2;

  &::before {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    z-index: -1;
  }
`;

const HeroImageWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LaningMatchupContainer = styled.div`
  flex: 1;
  background-color: #0b0d14;
  background-image: linear-gradient(to bottom, #161926, #0b0d14);
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  overflow-y: auto;
  border: 2px solid #61dafb;
`;

const LaningTitle = styled.h2`
  color: #61dafb;
  font-size: 24px;
  margin-bottom: 30px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const LanesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const LaneContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  padding: 20px;
  border: 2px solid ${props => {
    if (props.$lane === 'Safe') return 'rgba(29, 233, 182, 0.4)';
    if (props.$lane === 'Off') return 'rgba(255, 107, 107, 0.4)';
    return 'rgba(97, 218, 251, 0.4)';
  }};
  position: relative;
`;

const LaneHeader = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${props => {
    if (props.$lane === 'Safe') return '#1de9b6';
    if (props.$lane === 'Off') return '#ff6b6b';
    return '#61dafb';
  }};
  margin-bottom: 15px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const LaneMatchupGrid = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
`;

const TeamLaneSlots = styled.div`
  display: flex;
  gap: 10px;
`;

const LaneSlot = styled.div`
  width: 120px;
  height: 65px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 2px solid ${props => props.$team === 'Radiant' ? 'rgba(29, 233, 182, 0.5)' : 'rgba(255, 107, 107, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;

  &:hover {
    border-color: ${props => props.$team === 'Radiant' ? '#1de9b6' : '#ff6b6b'};
    background-color: rgba(255, 255, 255, 0.08);
    transform: scale(1.05);
  }

  ${props => props.$isEmpty && `
    &::after {
      content: '+';
      font-size: 36px;
      color: rgba(255, 255, 255, 0.3);
      position: absolute;
    }
  `}
`;

const VersusLabel = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #ffd700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
`;

const LanePositionLabel = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${props => props.$side === 'left' ? '20px' : 'auto'};
  right: ${props => props.$side === 'right' ? '20px' : 'auto'};
  font-size: 16px;
  font-weight: bold;
  color: ${props => props.$color || '#ff6b6b'};
  text-transform: lowercase;
  letter-spacing: 1px;
  opacity: 0.8;
`;

const PracticeMatchup = ({ isOpen, onClose, scenario, pickMode }) => {
  const { updateScenario } = useDraft();

  // State for laning matchups (separate from the main draft)
  const [laningMatchups, setLaningMatchups] = useState({
    safe: {
      radiant: [null, null],  // 2 heroes (carry + support)
      dire: [null, null]       // 2 heroes (offlane + support)
    },
    mid: {
      radiant: [null],         // 1 hero
      dire: [null]             // 1 hero
    },
    off: {
      radiant: [null, null],   // 2 heroes (offlane + support)
      dire: [null, null]       // 2 heroes (carry + support)
    }
  });

  const [showHeroSelect, setShowHeroSelect] = useState(false);
  const [selectingFor, setSelectingFor] = useState(null); // { lane, team, index }
  const [heroSearchQuery, setHeroSearchQuery] = useState('');

  // Load saved matchups or initialize when opened
  useEffect(() => {
    if (isOpen && scenario) {
      // Load saved practice matchups if they exist and have correct structure
      if (scenario.practiceMatchups && scenario.practiceMatchups.safe && scenario.practiceMatchups.mid && scenario.practiceMatchups.off) {
        console.log('🔍 Loading practice matchups from scenario:', scenario.practiceMatchups);
        setLaningMatchups(scenario.practiceMatchups);
      } else {
        console.log('⚠️ No valid practice matchups found in scenario, initializing empty');
        console.log('📋 Full scenario data:', scenario);
        // Initialize empty matchups
        setLaningMatchups({
          safe: { radiant: [null, null], dire: [null, null] },
          mid: { radiant: [null], dire: [null] },
          off: { radiant: [null, null], dire: [null, null] }
        });
      }
    }
  }, [isOpen, scenario]);

  // ESC key handler for closing modals
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showHeroSelect) {
          // Close hero selection modal first if open
          setShowHeroSelect(false);
          setSelectingFor(null);
          setHeroSearchQuery('');
        } else if (isOpen) {
          // Close practice matchup overlay if hero select is not open
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen, showHeroSelect, onClose]);

  const handleLaneSlotClick = (lane, team, index) => {
    setSelectingFor({ lane, team, index });
    setShowHeroSelect(true);
  };

  const handleHeroSelectFromGrid = (hero) => {
    handleHeroSelect(hero);
  };

  const savePracticeMatchups = (newMatchups) => {
    if (pickMode && scenario) {
      updateScenario(pickMode, scenario.id, { practiceMatchups: newMatchups });
    }
  };

  const handleHeroSelect = (hero) => {
    if (selectingFor) {
      const { lane, team, index } = selectingFor;
      const newMatchups = {
        ...laningMatchups,
        [lane]: {
          ...laningMatchups[lane],
          [team]: laningMatchups[lane][team].map((h, i) => i === index ? hero : h)
        }
      };
      setLaningMatchups(newMatchups);
      savePracticeMatchups(newMatchups);
      setShowHeroSelect(false);
      setSelectingFor(null);
      setHeroSearchQuery('');
    }
  };

  const clearLaneSlot = (lane, team, index) => {
    const newMatchups = {
      ...laningMatchups,
      [lane]: {
        ...laningMatchups[lane],
        [team]: laningMatchups[lane][team].map((h, i) => i === index ? null : h)
      }
    };
    setLaningMatchups(newMatchups);
    savePracticeMatchups(newMatchups);
  };

  if (!isOpen || !scenario || !scenario.picks) return null;

  return (
    <OverlayContainer onClick={onClose}>
      <ContentContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>

        {/* Left side: Scenario Board (read-only) */}
        <ScenarioBoardContainer>
          <ScenarioTitle>
            {pickMode === 'first' ? '1st Pick' : '2nd Pick'} - {scenario?.title || `Scenario ${scenario?.id}`}
          </ScenarioTitle>

          <TeamsContainer>
            <TeamHeader $team="Radiant">Radiant</TeamHeader>
            <TeamHeader $team="Dire">Dire</TeamHeader>
          </TeamsContainer>

          <PicksContainer>
            <TeamColumn $team="Radiant" $swapped={false}>
              {/* Radiant positions rendered left to right: 1, 2, 5, (spacer 6,7), 8, 10, 11, 14, 15, 18, 19, 21, 23 */}
              {[1, 2, 5, 8, 10, 11, 14, 15, 18, 19, 21, 23].map((position) => {
                const pick = scenario.picks[position - 1];
                const hero = pick?.hero || pick;
                const banPositions = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 19, 20, 21, 22];
                const isBanBox = banPositions.includes(position);
                const isLargeBox = DraftState.getLargeBoxSequences().includes(position.toString());
                const imageSize = isLargeBox ? 'large' : 'medium';

                return (
                  <PickSlot
                    key={position}
                    $team="Radiant"
                    $position={position}
                    className={hero ? 'filled' : ''}
                  >
                    <PositionLabel $isBan={isBanBox}>{position}</PositionLabel>
                    {hero && (
                      <HeroImageWrapper>
                        <HeroImage
                          hero={hero}
                          size={imageSize}
                          $grayscale={isBanBox}
                        />
                      </HeroImageWrapper>
                    )}
                    {isBanBox && hero && <BanCross>✕</BanCross>}
                  </PickSlot>
                );
              })}
            </TeamColumn>

            <TeamColumn $team="Dire" $swapped={false}>
              {/* Dire positions rendered left to right with spacers: (spacer 1), 3, 4, 6, 7, 9, (spacer 10), 12, 13, 16, 17, 20, 22, 24 */}
              {[3, 4, 6, 7, 9, 12, 13, 16, 17, 20, 22, 24].map((position) => {
                const pick = scenario.picks[position - 1];
                const hero = pick?.hero || pick;
                const banPositions = [1, 2, 3, 4, 5, 6, 7, 10, 11, 12, 19, 20, 21, 22];
                const isBanBox = banPositions.includes(position);
                const isLargeBox = DraftState.getLargeBoxSequences().includes(position.toString());
                const imageSize = isLargeBox ? 'large' : 'medium';

                return (
                  <PickSlot
                    key={position}
                    $team="Dire"
                    $position={position}
                    className={hero ? 'filled' : ''}
                  >
                    <PositionLabel $isBan={isBanBox}>{position}</PositionLabel>
                    {hero && (
                      <HeroImageWrapper>
                        <HeroImage
                          hero={hero}
                          size={imageSize}
                          $grayscale={isBanBox}
                        />
                      </HeroImageWrapper>
                    )}
                    {isBanBox && hero && <BanCross>✕</BanCross>}
                  </PickSlot>
                );
              })}
            </TeamColumn>
          </PicksContainer>
        </ScenarioBoardContainer>

        {/* Right side: Laning Matchups */}
        <LaningMatchupContainer>
          <LaningTitle>Practice Laning Matchups</LaningTitle>

          <LanesGrid>
            {/* Safe Lane */}
            <LaneContainer $lane="Safe">
              <LanePositionLabel $side="left" $color="#1de9b6">safelane</LanePositionLabel>
              <LaneMatchupGrid>
                <TeamLaneSlots>
                  {laningMatchups.safe.radiant.map((hero, index) => (
                    <LaneSlot
                      key={`safe-radiant-${index}`}
                      $team="Radiant"
                      $isEmpty={!hero}
                      onClick={() => handleLaneSlotClick('safe', 'radiant', index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (hero) clearLaneSlot('safe', 'radiant', index);
                      }}
                    >
                      {hero && (
                        <HeroImage
                          hero={hero}
                          width={110}
                          height={70}
                        />
                      )}
                    </LaneSlot>
                  ))}
                </TeamLaneSlots>

                <VersusLabel>VS</VersusLabel>

                <TeamLaneSlots>
                  {laningMatchups.safe.dire.map((hero, index) => (
                    <LaneSlot
                      key={`safe-dire-${index}`}
                      $team="Dire"
                      $isEmpty={!hero}
                      onClick={() => handleLaneSlotClick('safe', 'dire', index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (hero) clearLaneSlot('safe', 'dire', index);
                      }}
                    >
                      {hero && (
                        <HeroImage
                          hero={hero}
                          width={110}
                          height={70}
                        />
                      )}
                    </LaneSlot>
                  ))}
                </TeamLaneSlots>
              </LaneMatchupGrid>
              <LanePositionLabel $side="right" $color="#ff6b6b">offlane</LanePositionLabel>
            </LaneContainer>

            {/* Mid Lane */}
            <LaneContainer $lane="Mid">
              <LaneHeader $lane="Mid">Mid Lane</LaneHeader>
              <LaneMatchupGrid>
                <TeamLaneSlots>
                  {laningMatchups.mid.radiant.map((hero, index) => (
                    <LaneSlot
                      key={`mid-radiant-${index}`}
                      $team="Radiant"
                      $isEmpty={!hero}
                      onClick={() => handleLaneSlotClick('mid', 'radiant', index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (hero) clearLaneSlot('mid', 'radiant', index);
                      }}
                    >
                      {hero && (
                        <HeroImage
                          hero={hero}
                          width={110}
                          height={70}
                        />
                      )}
                    </LaneSlot>
                  ))}
                </TeamLaneSlots>

                <VersusLabel>VS</VersusLabel>

                <TeamLaneSlots>
                  {laningMatchups.mid.dire.map((hero, index) => (
                    <LaneSlot
                      key={`mid-dire-${index}`}
                      $team="Dire"
                      $isEmpty={!hero}
                      onClick={() => handleLaneSlotClick('mid', 'dire', index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (hero) clearLaneSlot('mid', 'dire', index);
                      }}
                    >
                      {hero && (
                        <HeroImage
                          hero={hero}
                          width={110}
                          height={70}
                        />
                      )}
                    </LaneSlot>
                  ))}
                </TeamLaneSlots>
              </LaneMatchupGrid>
            </LaneContainer>

            {/* Off Lane */}
            <LaneContainer $lane="Off">
              <LanePositionLabel $side="left" $color="#1de9b6">offlane</LanePositionLabel>
              <LaneMatchupGrid>
                <TeamLaneSlots>
                  {laningMatchups.off.radiant.map((hero, index) => (
                    <LaneSlot
                      key={`off-radiant-${index}`}
                      $team="Radiant"
                      $isEmpty={!hero}
                      onClick={() => handleLaneSlotClick('off', 'radiant', index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (hero) clearLaneSlot('off', 'radiant', index);
                      }}
                    >
                      {hero && (
                        <HeroImage
                          hero={hero}
                          width={110}
                          height={70}
                        />
                      )}
                    </LaneSlot>
                  ))}
                </TeamLaneSlots>

                <VersusLabel>VS</VersusLabel>

                <TeamLaneSlots>
                  {laningMatchups.off.dire.map((hero, index) => (
                    <LaneSlot
                      key={`off-dire-${index}`}
                      $team="Dire"
                      $isEmpty={!hero}
                      onClick={() => handleLaneSlotClick('off', 'dire', index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (hero) clearLaneSlot('off', 'dire', index);
                      }}
                    >
                      {hero && (
                        <HeroImage
                          hero={hero}
                          width={110}
                          height={70}
                        />
                      )}
                    </LaneSlot>
                  ))}
                </TeamLaneSlots>
              </LaneMatchupGrid>
              <LanePositionLabel $side="right" $color="#ff6b6b">safelane</LanePositionLabel>
            </LaneContainer>
          </LanesGrid>
        </LaningMatchupContainer>

        {/* Hero Selection Modal */}
        {showHeroSelect && (
          <>
            <HeroSelectOverlay onClick={() => {
              setShowHeroSelect(false);
              setHeroSearchQuery('');
            }} />
            <HeroSelectContainer onClick={(e) => e.stopPropagation()}>
              <HeroSelectHeader>
                <HeroSelectHeaderTop>
                  <HeroSelectTitle>
                    Select Hero for {selectingFor?.lane} Lane - {selectingFor?.team}
                  </HeroSelectTitle>
                  <CloseButtonSmall onClick={() => {
                    setShowHeroSelect(false);
                    setHeroSearchQuery('');
                  }}>
                    ×
                  </CloseButtonSmall>
                </HeroSelectHeaderTop>
                <SearchInput
                  type="text"
                  placeholder="Search heroes..."
                  value={heroSearchQuery}
                  onChange={(e) => setHeroSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && heroSearchQuery.trim()) {
                      // Filter and sort heroes same way as display
                      const filteredHeroes = Object.values(HeroData).filter(hero => {
                        const searchTerm = heroSearchQuery.toLowerCase().trim();
                        const heroName = hero.name.toLowerCase();

                        // Direct name match
                        if (heroName.includes(searchTerm)) return true;

                        // Split hero name into words
                        const words = heroName.split(/[\s\-']+/).filter(word => word.length > 0);
                        const abbreviation = words.map(word => word.charAt(0)).join('');

                        // Check abbreviation matches
                        if (abbreviation === searchTerm || abbreviation.startsWith(searchTerm)) return true;

                        // Check word start matches
                        if (words.some(word => word.startsWith(searchTerm))) return true;

                        // Restrictive for single char
                        if (searchTerm.length === 1) {
                          return abbreviation.startsWith(searchTerm) || words.some(word => word.startsWith(searchTerm));
                        }

                        return false;
                      }).sort((a, b) => {
                        const searchTerm = heroSearchQuery.toLowerCase().trim();
                        const aName = a.name.toLowerCase();
                        const bName = b.name.toLowerCase();

                        // Exact starts priority
                        const aStartsWith = aName.startsWith(searchTerm);
                        const bStartsWith = bName.startsWith(searchTerm);
                        if (aStartsWith && !bStartsWith) return -1;
                        if (bStartsWith && !aStartsWith) return 1;

                        // Abbreviation matches
                        const aWords = aName.split(/[\s\-']+/).filter(word => word.length > 0);
                        const bWords = bName.split(/[\s\-']+/).filter(word => word.length > 0);
                        const aAbbr = aWords.map(word => word.charAt(0)).join('');
                        const bAbbr = bWords.map(word => word.charAt(0)).join('');

                        const aAbbrMatch = aAbbr === searchTerm;
                        const bAbbrMatch = bAbbr === searchTerm;
                        if (aAbbrMatch && !bAbbrMatch) return -1;
                        if (bAbbrMatch && !aAbbrMatch) return 1;

                        // Alphabetical
                        return aName.localeCompare(bName);
                      });

                      // Select first hero if any matches
                      if (filteredHeroes.length > 0) {
                        handleHeroSelectFromGrid(filteredHeroes[0]);
                      }
                    }
                  }}
                  autoFocus
                />
              </HeroSelectHeader>
              <HeroGrid>
                {(heroSearchQuery
                  ? Object.values(HeroData).filter(hero => {
                      const searchTerm = heroSearchQuery.toLowerCase().trim();
                      const heroName = hero.name.toLowerCase();

                      // Direct name match
                      if (heroName.includes(searchTerm)) {
                        return true;
                      }

                      // Split hero name into words (handle spaces, hyphens, and apostrophes)
                      const words = heroName.split(/[\s\-']+/).filter(word => word.length > 0);

                      // Create abbreviation from first letters
                      const abbreviation = words.map(word => word.charAt(0)).join('');

                      // Check exact abbreviation match (e.g., "aa" -> "Ancient Apparition")
                      if (abbreviation === searchTerm) {
                        return true;
                      }

                      // Check partial abbreviation match (e.g., "a" matches "Ancient Apparition")
                      if (abbreviation.startsWith(searchTerm)) {
                        return true;
                      }

                      // Check if search term matches any word start
                      if (words.some(word => word.startsWith(searchTerm))) {
                        return true;
                      }

                      // Special case: if search is very short, be more restrictive
                      if (searchTerm.length === 1) {
                        return abbreviation.startsWith(searchTerm) || words.some(word => word.startsWith(searchTerm));
                      }

                      return false;
                    }).sort((a, b) => {
                      // Sort results by relevance
                      const searchTerm = heroSearchQuery.toLowerCase().trim();
                      const aName = a.name.toLowerCase();
                      const bName = b.name.toLowerCase();

                      // Exact name starts get priority
                      const aStartsWith = aName.startsWith(searchTerm);
                      const bStartsWith = bName.startsWith(searchTerm);
                      if (aStartsWith && !bStartsWith) return -1;
                      if (bStartsWith && !aStartsWith) return 1;

                      // Then abbreviation matches
                      const aWords = aName.split(/[\s\-']+/).filter(word => word.length > 0);
                      const bWords = bName.split(/[\s\-']+/).filter(word => word.length > 0);
                      const aAbbr = aWords.map(word => word.charAt(0)).join('');
                      const bAbbr = bWords.map(word => word.charAt(0)).join('');

                      const aAbbrMatch = aAbbr === searchTerm;
                      const bAbbrMatch = bAbbr === searchTerm;
                      if (aAbbrMatch && !bAbbrMatch) return -1;
                      if (bAbbrMatch && !aAbbrMatch) return 1;

                      // Finally alphabetical
                      return aName.localeCompare(bName);
                    })
                  : Object.values(HeroData)
                ).map(hero => (
                  <HeroSelectCard
                    key={hero.id}
                    onClick={() => handleHeroSelectFromGrid(hero)}
                  >
                    <HeroImage hero={hero} size="small" />
                    <HeroName>{hero.name}</HeroName>
                  </HeroSelectCard>
                ))}
              </HeroGrid>
            </HeroSelectContainer>
          </>
        )}
      </ContentContainer>
    </OverlayContainer>
  );
};

const HeroSelectOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroSelectContainer = styled.div`
  position: fixed;
  z-index: 10003;
  width: 90vw;
  max-width: 500px;
  max-height: 80vh;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(14, 16, 22, 0.97);
  border: 1px solid #444;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  animation: popupFadeIn 0.25s cubic-bezier(0.23, 1, 0.32, 1);

  @keyframes popupFadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
  }
`;

const HeroSelectHeader = styled.div`
  padding: 8px 16px;
  border-bottom: 1px solid #444;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: rgba(0, 0, 0, 0.3);
`;

const HeroSelectHeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid #444;
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #61dafb;
    background-color: rgba(255, 255, 255, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const HeroSelectTitle = styled.div`
  font-weight: bold;
  color: #eee;
  font-size: 14px;
`;

const CloseButtonSmall = styled.div`
  cursor: pointer;
  font-size: 20px;
  color: #888;
  padding: 0 8px;
  transition: color 0.2s;

  &:hover {
    color: #fff;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 100px));
  gap: 10px;
  padding: 16px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 400px;
  flex: 1;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #555;
    border-radius: 4px;

    &:hover {
      background-color: #666;
    }
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 4px;
  }
`;

const HeroSelectCard = styled.div`
  cursor: pointer;
  padding: 6px 4px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(1px);
  }
`;

const HeroName = styled.div`
  font-size: 11px;
  margin-top: 6px;
  text-align: center;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  font-weight: 500;
`;

export default PracticeMatchup;
